/**
 * /anfragen-Drawer · Reiter „Gesprächsprotokoll" (CRM 2.0, Paket 23.10.2026 a)
 *
 * Oben „Kunde anrufen" (PhoneLink: 3CX Click-to-Call, Fallback tel:), darunter
 * die Gesprächsprotokolle des Kunden (gemeinsame Komponente, Familienkonto:
 * Gespräche aller Mitglieder). Nach dem Klick auf „Kunde anrufen" führt ein
 * kleiner Ablauf durch den Nachlauf:
 *
 *   idle → dialed („Anruf beendet"-Knopf) → ended (Liste neu laden, Hinweis +
 *   „E-Mail aus Vorlage senden") → EmailVorlageModal (Vorlage wählen, Vorschau,
 *   senden) → idle
 *
 * Der Mailversand nutzt den bestehenden v2-Vorlagenversand
 * (POST /v2/admin/email-templates/{id}/send), es gibt keinen eigenen Endpoint.
 * Das Modal wird per Portal in den body gerendert: der Drawer-Panel behält
 * durch die fade-in-Animation ein transform und würde fixed-Kinder sonst in
 * sich einsperren (Containing-Block-Falle).
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui';
import { PhoneLink } from '../../components/PhoneLink';
import { Gespraechsprotokolle } from '../../components/calls/Gespraechsprotokolle';
import { EmailVorlageModal } from '../pflege/tabs/EmailVorlageModal';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../lib/theme';
import { toast } from '../../lib/toast';
import type { V1User } from '../../lib/v1';

type CallStep = 'idle' | 'dialed' | 'ended';

interface Props {
  /** Person, die angerufen/angemailt wird (Anfrage-User bzw. Pflegeempfänger). */
  contact: V1User;
  /** Rufnummer (Schnell-bearbeiten-Stand, sonst Mobil/Telefon des Users). */
  phone: string;
  dark: boolean;
}

export function AnfrageGespraechTab({ contact, phone, dark }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [step, setStep] = useState<CallStep>('idle');
  const [reloadSignal, setReloadSignal] = useState(0);
  const [showMail, setShowMail] = useState(false);
  const hasEmail = Boolean(contact.email);

  const finishCall = () => {
    setStep('ended');
    // Gespräch und ggf. frisch eingetroffene Aufnahme sofort in der Liste zeigen.
    setReloadSignal(n => n + 1);
  };

  const bannerStyle: React.CSSProperties = {
    marginTop: 10, padding: '10px 12px', borderRadius: 9,
    background: dark ? '#1e2a4a' : BRAND_LIGHT, border: `1px solid ${t.border}`,
    fontSize: 12.5, color: t.text, display: 'flex', flexDirection: 'column', gap: 8,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section>
        {phone ? (
          <PhoneLink
            number={phone}
            dark={dark}
            showIcon
            label={tr('call_history.call_customer')}
            onDial={() => setStep(s => (s === 'idle' ? 'dialed' : s))}
            style={{
              background: BRAND, color: '#fff', borderRadius: 9, padding: '9px 16px',
              fontSize: 13, fontWeight: 600, fontFamily: 'Outfit,sans-serif',
            }}
          />
        ) : (
          <div data-testid="no-phone" style={{ fontSize: 12.5, color: t.muted, fontStyle: 'italic' }}>
            {tr('call_history.no_phone')}
          </div>
        )}
        {phone && <div style={{ marginTop: 6, fontSize: 12, color: t.muted }}>{phone}</div>}

        {step === 'dialed' && (
          <div style={bannerStyle} data-testid="call-step-dialed">
            <span>{tr('call_history.call_started')}</span>
            <div>
              <Button variant="secondary" size="sm" dark={dark} onClick={finishCall}>
                {tr('call_history.call_ended')}
              </Button>
            </div>
          </div>
        )}

        {step === 'ended' && (
          <div style={bannerStyle} data-testid="call-step-ended">
            <span>{hasEmail ? tr('call_history.after_call_hint') : tr('call_history.no_email')}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {hasEmail && (
                <Button variant="primary" size="sm" dark={dark} onClick={() => setShowMail(true)}>
                  {tr('call_history.send_email')}
                </Button>
              )}
              <Button variant="secondary" size="sm" dark={dark} onClick={() => setStep('idle')}>
                {tr('call_history.dismiss')}
              </Button>
            </div>
          </div>
        )}
      </section>

      <Gespraechsprotokolle
        userId={contact.id}
        dark={dark}
        variant="plain"
        maxHeight={0}
        reloadSignal={reloadSignal}
      />

      {showMail && hasEmail && createPortal(
        <EmailVorlageModal
          dark={dark}
          user={contact}
          onClose={() => setShowMail(false)}
          onSent={() => {
            setShowMail(false);
            setStep('idle');
            toast(tr('call_history.email_sent'), 'success');
          }}
        />,
        document.body,
      )}
    </div>
  );
}
